import React, { useEffect, useMemo, useState } from 'react';

import MerchantSelect from '@components/operator/MerchantSelect';
import ReturnOrderEdit from '@components/shared/return_orders/ReturnOrderEdit';
import PageHeader from '@shared/PageHeader';
import { useOperatorData } from '@contexts/operator/OperatorDataContext';
import { useOperatorReturnOrderService } from '@hooks/operator/return_orders/useOperatorReturnOrderService';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';
import useRecordMerchantSync from '@hooks/operator/useRecordMerchantSync';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import operatorInventoryAttributeDefinitionService from '@services/operator/inventoryAttributeDefinitionService';

interface InventoryTypeOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface MerchantInventoryType {
  merchant_id: string;
  enabled?: boolean;
  inventory_type: InventoryTypeOption;
}

interface InventoryAttributeDefinition {
  id?: string;
  name: string;
  [key: string]: unknown;
}

// Operator RO Edit wrapper. 兩個模式：
//   1. id 存在（編輯）：用 useRecordMerchantSync 將 OperatorDataContext.currentMerchant
//      對齊到 record 的 merchant；merchantDataLoading 期間 shared 元件 spinner 擋住表單，
//      避免 merchant-scoped 下拉先用 header 當下選的 merchant 渲染後再切。
//   2. id 不存在（新建）：用 MerchantSelect 讓使用者主動挑 merchant；不需要 sync。
const OperatorReturnOrderEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();

  const { merchantInventoryTypes, currentMerchant } = useOperatorData();
  const { handleRecordLoaded, merchantDataLoading } = useRecordMerchantSync();

  // currentMerchant 在 edit 模式下會被 useRecordMerchantSync 切到 record 所屬 merchant，
  // 在 new 模式下由 MerchantSelect 設定。Reference data 永遠跟著 currentMerchant 走。
  const effectiveMerchantId = currentMerchant?.id ?? null;

  const inventoryTypes = useMemo(() => {
    if (!effectiveMerchantId) return [];
    return (merchantInventoryTypes as MerchantInventoryType[] | undefined ?? [])
      .filter(mit => mit.merchant_id === effectiveMerchantId && mit.enabled !== false)
      .map(mit => mit.inventory_type);
  }, [merchantInventoryTypes, effectiveMerchantId]);

  const [inventoryAttributeDefinitions, setInventoryAttributeDefinitions] =
    useState<InventoryAttributeDefinition[]>([]);

  useEffect(() => {
    if (!effectiveMerchantId) {
      setInventoryAttributeDefinitions([]);
      return;
    }
    let cancelled = false;
    operatorInventoryAttributeDefinitionService
      .fetchList({ merchant_id: effectiveMerchantId })
      .then((result: { success?: boolean; data?: InventoryAttributeDefinition[] }) => {
        if (cancelled) return;
        setInventoryAttributeDefinitions(result?.data ?? []);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to fetch inventory attribute definitions', error);
        setInventoryAttributeDefinitions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveMerchantId]);

  // 編輯模式：wrapper 由 onReturnOrderLoaded 拿到 record's merchant_id 後同步 context，
  // 整個過程 merchantDataLoading 為 true 擋住 form 渲染，避免閃錯的下拉。
  if (id) {
    return (
      <div>
        <PageHeader items={[ 'returns', 'returns.list', { label: t('common.button.edit') } ]} />
        <ReturnOrderEdit
          useReturnOrderService={useOperatorReturnOrderService}
          useProductService={useOperatorProductService}
          role="operator"
          merchantId={effectiveMerchantId}
          inventoryTypes={inventoryTypes}
          inventoryAttributeDefinitions={inventoryAttributeDefinitions}
          onReturnOrderLoaded={handleRecordLoaded}
          merchantDataLoading={merchantDataLoading}
        />
      </div>
    );
  }

  // 新增模式：MerchantSelect 控制 merchant；record 還不存在，不需要 sync
  return (
    <MerchantSelect>
      <div>
        <PageHeader items={[ 'returns', 'returns.list', { label: t('operator.menu.return_orders.new') } ]} />
        <ReturnOrderEdit
          useReturnOrderService={useOperatorReturnOrderService}
          useProductService={useOperatorProductService}
          role="operator"
          merchantId={effectiveMerchantId}
          inventoryTypes={inventoryTypes}
          inventoryAttributeDefinitions={inventoryAttributeDefinitions}
        />
      </div>
    </MerchantSelect>
  );
};

export default OperatorReturnOrderEditPage;
